import { forwardRef, useMemo } from "react";

import { BoxProps } from "@mui/material/Box";
import Divider, { DividerProps } from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";

import { getNestedValueFromPalette, ZeffyColorTokenBorderClasses } from "@/dashboard-design-system/helpers";
import { mergeSx } from "@/helpers/mergeSx";

export type DashboardDividerProps = Omit<DividerProps, "vibe"> & {
  vibe?: ZeffyColorTokenBorderClasses | "currentColor";
  borderColor?: BoxProps["borderColor"];
};

export const DashboardDivider = forwardRef<HTMLHRElement, DashboardDividerProps>(
  ({ vibe = "border-neutral-quiet", sx, borderColor, ...rest }, ref) => {
    const theme = useTheme();

    const vibeColor = useMemo(() => {
      if (vibe === "currentColor") {
        return "currentColor";
      }

      const borderColor = getNestedValueFromPalette(theme.palette, vibe);
      if (!borderColor) {
        throw new Error(`Invalid vibe: ${vibe}`);
      }

      return borderColor;
    }, [vibe, theme]);

    const styles = mergeSx(
      {
        ["&, &:before, &:after"]: {
          color: borderColor ?? vibeColor,
          borderColor: borderColor ?? vibeColor,
        },
      },
      sx
    );

    return <Divider ref={ref} sx={styles} {...rest} />;
  }
);

DashboardDivider.displayName = "DashboardDivider";
